/* eslint-disable */
import { i18n } from '@/commons/utils';
import { askLocation } from '@/commons/user';
import { getConfiguration } from './ac-configuration';
import { templateAutocomplete, templateSuggestion } from './ac-templates';

export class AutocompleteGeneric {
  /**
      Autocomplete Constructor
   */
  constructor($root, options) {
    // init Autocomplete DOM
    $root.innerHTML = templateAutocomplete;

    // get DOMElements
    this.$root = $root;
    this.$form = $root.$('.dkt-form');
    this.$suggestions = $root.$('.js-suggestions');
    this.$input = $root.$('.js-input');
    this.$locateMe = $root.$('.js-locate-me');

    // country specific configuration
    this.config = getConfiguration();
    // contains all Autocomplete events
    this.mapEvents = {};
    // list of received suggestions
    this.suggestions = [];
    // flag that determines if a click is outside a suggestion
    // (in order to close [or not] the suggestion list)
    this.isSuggestionClick = false;

    // add 'loading' state to $form
    this.setLoadingState(true);

    // get Autocomplete libraries
    this.getLibrary()
      .then(() => {
        this.setLoadingState(false);

        // bind interactions
        this.bindInput();
        this.bindClick();
        this.bindGeoloc();
      })
      .catch(error =>
        // catch any error on init
        console.error(`Error while initing [${this.constructor.name}]`, error)
      );

    // bind 'input blur' :
    // remove suggestions list when input lose focus
    this.$input.on('blur', () => {
      setTimeout(() => {
        if (!this.isSuggestionClick) {
          this.$input.value = '';
          this.removeSuggestions();
        }
      }, 300);
    });
  }

  /**
    Handle Events
   */

  on(event, callback) {
    if (!this.mapEvents[event]) this.mapEvents[event] = [];
    this.mapEvents[event].push(callback);
  }
  emit(event, ...args) {
    if (this.mapEvents[event])
      this.mapEvents[event].forEach(callback => callback(...args));
  }

  /**
   *  Country-commons Methods
   */

  // init 'listening input field'
  bindInput() {
    this.$input.on(
      'input',
      cube.debounce(() => {
        // get input value
        const inputValue = String(this.$input.value).trim();
        // reinit flag
        this.isSuggestionClick = false;

        // Exit / Remove suggestions if input is not good enought
        if (inputValue.length < this.config.inputMinLength)
          return this.removeSuggestions();

        // Notes for the few following lines of (effective) code
        // ** callback ends on a Promise **
        //  - Promise "will" be resolved with the founded suggestions
        //  - Promise is rejected after every new (future) keystrokes :
        //      - the reference of the previou 'reject' is kept inside 'this.previousInputReject'
        //      - rejecting it after every new input entry will "desactivate" the previous 'resolve' function,
        //        it will still be valid / callable, but it won't do anything !
        //        This avoid :
        //          - Useless Autocomplete refresh (new input > new search : previous become useless)
        //          - memory leaks : every generated promise is properly ended
        //      - if a promise is resolved first, calling its related 'reject' will simply do nothing

        // reject the previous 'reject'
        if (this.previousInputReject) this.previousInputReject();

        // return the Promise, that will call "your" #searchSuggestions method (immediatly)
        // "your" method receive : input / resolve / reject :
        //  - search your location with the input
        //  - "send" your suggestion throught the 'resolve' function : resolve(suggestions);
        //  - you can still handle errors with the 'reject' function, but it will be called anyway after a new input entry
        return new Promise((resolve, reject) => {
          this.previousInputReject = reject;
          this.searchSuggestions(inputValue, resolve, reject);
        })
          .then(suggs => {
            this.setSuggestions(suggs);
          })
          .catch(() => {
            // Do'nt do anyhting here
          });
      }, this.config.inputDebounce)
    );
  }

  // init 'listening click on suggestions'
  bindClick() {
    this.$suggestions.on('click', e => {
      // get index of cliked suggestion
      const index = Array.from(this.$suggestions.children).indexOf(e.target);
      const suggestion = this.suggestions[index];

      // exit if no suggestion
      if (!suggestion) return;
      // set flag 'suggestion click'
      this.isSuggestionClick = true;

      this.setLoadingState(true);

      // same mechanism as above (previousInputReject)
      if (this.previousClickReject) this.previousClickReject();

      // Promise
      new Promise((resolve, reject) => {
        this.previousClickReject = reject;
        this.searchPosition(suggestion, resolve, reject);
      })
        .then(position => {
          this.setLoadingState(false);
          // fill input value with position's name
          if (position) this.$input.value = position.name;
          // remove suggestions list
          this.removeSuggestions();
          // send 'select' event
          this.emit('select', position);
        })
        .catch(() => {});
    });
  }

  // init 'listening clik on geoloc-me button'
  bindGeoloc() {
    this.$locateMe.on('click', () => {
      this.$form.classList.add('store-selection__search-form--loading');
      askLocation()
        .then(position => {
          // fill input value with position's name
          if (position) this.$input.value = position.name;
          // remove suggestions list
          this.removeSuggestions();
          // send 'select' event
          this.emit('select', position);
        })
        .catch(() => {
          this.$form.classList.remove('store-selection__search-form--loading');
          cube.alert.error(i18n('woosmap.search.geolocation.error'));
        });
    });
  }

  // Common when receiving suggestions
  setSuggestions(suggestions) {
    // save suggestions -- we will access them on click
    this.suggestions = suggestions;
    // remove previous suggestions
    this.removeSuggestions();
    // display new suggestions -- if any
    if (suggestions) suggestions.forEach(s => this.addSuggestion(s));
    // send 'suggest' event
    this.emit('suggest', suggestions);
  }

  // Add a suggestion to their container
  addSuggestion(suggestion) {
    // redisplay container to show elements in
    this.$suggestions.classList.add('filled-list');
    this.$suggestions.insertAdjacentHTML(
      'beforeend',
      templateSuggestion(suggestion)
    );
  }

  // Remove suggestions from DOM
  removeSuggestions() {
    Array.from((this.$suggestions || {}).children || []).forEach(child =>
      child.remove()
    );
    // undisplay when the container is empty
    this.$suggestions.classList.remove('filled-list');
  }

  // enable / disable 'loading' state
  setLoadingState(isLoading) {
    const action = isLoading ? 'add' : 'remove';
    this.$form.classList[action]('store-selection__search-form--loading');
    this.$input.disabled = isLoading;
  }

  /**
   *  Country-specific Methods
   */

  // ! \\ must be overwritten
  // get the scripts that will init the Autocomplete
  getLibrary() {
    logNotReimplemented(this.constructor.name, 'getLibrary');
    return Promise.resolve();
  }

  // ! \\ must be overwritten
  // receive an 'input' value and returns an array of 'suggestions'
  searchSuggestions() {
    logNotReimplemented(this.constructor.name, 'searchSuggestions');
    return Promise.resolve([]);
  }

  // ! \\ must be overwritten
  // receive a 'suggestion' and returns a 'position' object fully detailed
  searchPosition() {
    logNotReimplemented(this.constructor.name, 'searchPosition');
    return Promise.resolve();
  }
}

const logNotReimplemented = (constructor, method) => {
  console.error(`Error ${constructor}`);
  console.warn(`${method} method is not re-implemented on your autocomplete`);
};



// WEBPACK FOOTER //
// ./src/commons/component/autocomplete/ac-generic.js